// pt-BR copy for the error codes the public Edge Functions return
// (sign-budget, sign-contract, mp-public-pay, submit-lead). The token-gated
// pages used to print the raw code ("expired", "already_completed") to the
// client — this is the single map they now go through.

export type PublicErrorKind = "expired" | "done" | "blocked" | "invalid" | "generic";

export interface PublicErrorCopy {
  kind: PublicErrorKind;
  title: string;
  message: string;
}

const MAP: Record<string, PublicErrorCopy> = {
  expired: {
    kind: "expired",
    title: "Este link expirou",
    message:
      "Por segurança, links de assinatura e pagamento têm validade limitada. Peça um novo link para Eliveuton.",
  },
  not_found: {
    kind: "invalid",
    title: "Link inválido",
    message: "Não encontramos esta solicitação. Confira se o link foi copiado por completo.",
  },
  already_completed: {
    kind: "done",
    title: "Esta solicitação já foi concluída",
    message: "Sua resposta já foi registrada. Se precisar de uma nova via, fale com Eliveuton.",
  },
  already_paid: {
    kind: "done",
    title: "Esta parcela já foi paga",
    message: "Obrigado! Não há mais nada a fazer aqui.",
  },
  waiting_previous_signer: {
    kind: "blocked",
    title: "Aguardando o signatário anterior",
    message:
      "Esta assinatura é sequencial: você poderá assinar assim que a pessoa anterior concluir.",
  },
  rate_limited: {
    kind: "blocked",
    title: "Muitas tentativas",
    message: "Aguarde alguns minutos e tente novamente.",
  },
  reason_required: {
    kind: "invalid",
    title: "Informe o motivo",
    message: "Para recusar, descreva brevemente o motivo.",
  },
  invalid_signature_type: {
    kind: "invalid",
    title: "Tipo de assinatura inválido",
    message: "Escolha desenhar, digitar ou usar certificado digital.",
  },
  invalid_signature_data: {
    kind: "invalid",
    title: "Assinatura incompleta",
    message: "Desenhe ou digite sua assinatura antes de confirmar.",
  },
  certificate_verification_failed: {
    kind: "invalid",
    title: "Certificado não verificado",
    message: "Não foi possível validar o certificado digital. Confira o arquivo e a senha.",
  },
  invalid_payload: {
    kind: "invalid",
    title: "Dados incompletos",
    message: "Confira nome, e-mail e mensagem e tente novamente.",
  },
  mp_error: {
    kind: "generic",
    title: "Erro no Mercado Pago",
    message: "O pagamento não pôde ser iniciado agora. Tente novamente em instantes.",
  },
  not_payable: {
    kind: "done",
    title: "Esta parcela não está mais disponível",
    message:
      "A cobrança foi cancelada ou estornada. Fale com Eliveuton se precisar de um novo link.",
  },
  token_required: {
    kind: "invalid",
    title: "Link inválido",
    message: "Não encontramos esta solicitação. Confira se o link foi copiado por completo.",
  },
  invalid_action: {
    kind: "invalid",
    title: "Ação inválida",
    message: "Recarregue a página e tente novamente.",
  },
  captcha_required: {
    kind: "invalid",
    title: "Confirme que você não é um robô",
    message: "Conclua a verificação de segurança antes de enviar.",
  },
  captcha_invalid: {
    kind: "invalid",
    title: "Verificação de segurança falhou",
    message: "A verificação expirou ou não foi aceita. Refaça e envie de novo.",
  },
  captcha_unavailable: {
    kind: "generic",
    title: "Verificação indisponível",
    message: "Não foi possível validar o envio agora. Tente em instantes ou fale pelo WhatsApp.",
  },
};

const GENERIC: PublicErrorCopy = {
  kind: "generic",
  title: "Algo deu errado",
  message: "Não foi possível concluir a ação. Tente novamente ou fale com Eliveuton.",
};

/** Maps an Edge Function error code (or free-form message) to user-facing copy. */
export function publicError(code: string | null | undefined): PublicErrorCopy {
  if (!code) return GENERIC;
  return MAP[code] ?? GENERIC;
}

/** Short one-liner for toasts. */
export function publicErrorMessage(code: string | null | undefined): string {
  const e = publicError(code);
  return e.kind === "generic" && code && !MAP[code] ? `${e.message} (${code})` : e.message;
}
